/**
 * Scheda "Test" del dettaglio webinar: editor delle domande (prima della chiusura della diretta),
 * riepilogo degli esiti e tabella per iscritto (dopo), chiusura anticipata.
 */
import type { DomandeTestInput, EsitoIscritto, TestAdmin as TestAdminDati } from '@aulaviva/shared';
import { domandeTestInputSchema, REGOLE_TEST } from '@aulaviva/shared';
import { Alert, Badge, Button, Card, DataTable, Dialog, EmptyState, NativeSelect, StatBlock, StatRow, TextArea, TextField, useToast, type ColumnDef } from '@aulaviva/ui';
import { ArrowDown, ArrowUp, CircleCheck, CircleX, Clock, ClipboardList, Lock, Plus, Save, Square, Trash2, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { ErroreRichiesta } from '@/api/client';
import { useChiudiTest, useEsitiTest, useRiepilogoTest, useSalvaTestAdmin, useTestAdmin } from '@/api/test';
import { formattaDataOra, formattaOra } from '@/lib/date';
import { admin, comuni, test as t } from '@/testi';
import { CampoRicerca } from './comuni';

type DomandaBozza = { testo: string; risposte: { testo: string; corretta: boolean }[] };

function domandaVuota(): DomandaBozza {
  return { testo: '', risposte: [{ testo: '', corretta: true }, { testo: '', corretta: false }, { testo: '', corretta: false }, { testo: '', corretta: false }] };
}

export function SchedaTest({ webinarId, terminato }: { webinarId: string; terminato: boolean }) {
  const dati = useTestAdmin(webinarId);
  if (dati.isPending) return <p className="t-body text-ink-muted">{comuni.caricamento}</p>;
  if (dati.isError) return <Alert variant="error" title={dati.error.message} action={<Button onClick={() => dati.refetch()}>{comuni.riprova}</Button>} />;
  return (
    <>
      {terminato || !dati.data.modificabile ? <SezioneEsiti webinarId={webinarId} dati={dati.data} /> : null}
      <EditorDomande webinarId={webinarId} dati={dati.data} />
    </>
  );
}

function EditorDomande({ webinarId, dati }: { webinarId: string; dati: TestAdminDati }) {
  const ta = t.admin;
  const salva = useSalvaTestAdmin(webinarId);
  const toast = useToast();
  const [domande, setDomande] = useState<DomandaBozza[]>(() => dati.domande.map((d) => ({ testo: d.testo, risposte: d.risposte.map((r) => ({ testo: r.testo, corretta: r.corretta })) })));
  const [corretteMinime, setCorretteMinime] = useState(dati.corretteMinime);
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreGenerale, setErroreGenerale] = useState<string | null>(null);
  const bloccato = !dati.modificabile;

  useEffect(() => {
    setDomande(dati.domande.map((d) => ({ testo: d.testo, risposte: d.risposte.map((r) => ({ testo: r.testo, corretta: r.corretta })) })));
    setCorretteMinime(dati.corretteMinime);
  }, [dati]);

  function aggiorna(i: number, fn: (d: DomandaBozza) => DomandaBozza) {
    setDomande((ds) => ds.map((d, k) => (k === i ? fn(d) : d)));
  }
  function sposta(i: number, delta: number) {
    setDomande((ds) => {
      const j = i + delta;
      if (j < 0 || j >= ds.length) return ds;
      const out = [...ds];
      [out[i], out[j]] = [out[j]!, out[i]!];
      return out;
    });
  }

  async function onSalva() {
    setErroreGenerale(null);
    const input: DomandeTestInput = { domande, corretteMinime };
    const esito = domandeTestInputSchema.safeParse(input);
    if (!esito.success) {
      const e: Record<string, string> = {};
      for (const issue of esito.error.issues) e[issue.path.join('.')] = issue.message;
      setErrori(e);
      return;
    }
    if (corretteMinime > domande.length) {
      setErrori({ corretteMinime: ta.errori.minimeTroppoAlte });
      return;
    }
    setErrori({});
    try {
      await salva.mutateAsync(esito.data);
      toast.mostra({ variant: 'success', title: ta.salvato });
    } catch (e) {
      setErroreGenerale(e instanceof ErroreRichiesta && e.codice === 'test_non_modificabile' ? ta.bloccato : e instanceof Error ? e.message : comuni.erroreGenerico);
    }
  }

  return (
    <section aria-labelledby="editor-test" className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 id="editor-test" className="t-title-2">
          {ta.titolo}
        </h2>
        <p className="t-body text-ink-muted">{ta.sottotitolo}</p>
        <Badge variant={bloccato ? 'neutral' : 'success'} icon={bloccato ? Lock : CircleCheck}>
          {bloccato ? ta.bloccato : ta.modificabile}
        </Badge>
      </div>
      {!dati.pronto && !bloccato && <Alert variant="warning" title={ta.nonPronto} />}
      {domande.length !== REGOLE_TEST.domandePerTest && domande.length > 0 && <Alert variant="warning" title={ta.avvisoNumero(domande.length, REGOLE_TEST.domandePerTest)} />}
      {erroreGenerale && <Alert variant="error" title={erroreGenerale} />}
      {domande.length === 0 && <EmptyState icon={ClipboardList} title={ta.nessunaDomanda} />}
      {domande.map((d, i) => {
        const prefisso = `domande.${i}`;
        return (
          <Card key={i} className="flex flex-col gap-4" data-domanda-editor={i + 1}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="t-title-3">{ta.domanda(i + 1)}</h3>
              {!bloccato && (
                <div className="flex gap-1">
                  <Button variant="discreet" icon={ArrowUp} onClick={() => sposta(i, -1)} disabled={i === 0} aria-label={`${ta.su}: ${ta.domanda(i + 1)}`}>
                    {ta.su}
                  </Button>
                  <Button variant="discreet" icon={ArrowDown} onClick={() => sposta(i, 1)} disabled={i === domande.length - 1} aria-label={`${ta.giu}: ${ta.domanda(i + 1)}`}>
                    {ta.giu}
                  </Button>
                  <Button variant="discreet" icon={Trash2} onClick={() => setDomande((ds) => ds.filter((_, k) => k !== i))} aria-label={`${ta.rimuoviDomanda}: ${ta.domanda(i + 1)}`}>
                    {ta.rimuoviDomanda}
                  </Button>
                </div>
              )}
            </div>
            <TextArea label={ta.testoDomanda} value={d.testo} onChange={(e) => aggiorna(i, (x) => ({ ...x, testo: e.target.value }))} rows={2} disabled={bloccato} error={errori[`${prefisso}.testo`]} maxLength={1000} />
            {errori[`${prefisso}.risposte`] && <p role="alert" className="t-body-small text-error">{errori[`${prefisso}.risposte`]}</p>}
            <div role="radiogroup" aria-label={`${ta.domanda(i + 1)}: ${ta.corretta}`} className="flex flex-col gap-3">
              {d.risposte.map((r, k) => (
                <div key={k} className="flex items-start gap-3">
                  <label className="inline-flex items-center gap-2 min-h-[48px] pt-7 shrink-0 t-body-small">
                    <input
                      type="radio"
                      name={`corretta-${webinarId}-${i}`}
                      checked={r.corretta}
                      disabled={bloccato}
                      onChange={() => aggiorna(i, (x) => ({ ...x, risposte: x.risposte.map((rr, kk) => ({ ...rr, corretta: kk === k })) }))}
                      aria-label={ta.segnaCorretta(k + 1)}
                      className="h-5 w-5 accent-[var(--accent)]"
                    />
                    {ta.corretta}
                  </label>
                  <TextField label={ta.risposta(k + 1)} value={r.testo} onChange={(e) => aggiorna(i, (x) => ({ ...x, risposte: x.risposte.map((rr, kk) => (kk === k ? { ...rr, testo: e.target.value } : rr)) }))} disabled={bloccato} error={errori[`${prefisso}.risposte.${k}.testo`]} maxLength={500} className="flex-1" />
                  {!bloccato && d.risposte.length > REGOLE_TEST.risposteMinimePerDomanda && (
                    <Button variant="discreet" icon={Trash2} className="mt-7" onClick={() => aggiorna(i, (x) => { const risposte = x.risposte.filter((_, kk) => kk !== k); if (!risposte.some((rr) => rr.corretta) && risposte[0]) risposte[0] = { ...risposte[0], corretta: true }; return { ...x, risposte }; })} aria-label={`${ta.rimuoviRisposta} ${k + 1}`}>
                      {ta.rimuoviRisposta}
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {!bloccato && d.risposte.length < REGOLE_TEST.risposteMassimePerDomanda && (
              <Button variant="discreet" icon={Plus} onClick={() => aggiorna(i, (x) => ({ ...x, risposte: [...x.risposte, { testo: '', corretta: false }] }))} className="self-start">
                {ta.aggiungiRisposta}
              </Button>
            )}
          </Card>
        );
      })}
      {!bloccato && (
        <>
          <Button icon={Plus} onClick={() => setDomande((ds) => [...ds, domandaVuota()])} className="self-start">
            {ta.aggiungiDomanda}
          </Button>
          <TextField label={ta.corretteMinime} hint={ta.corretteMinimeAiuto} type="number" min={1} max={20} value={corretteMinime} onChange={(e) => setCorretteMinime(Number(e.target.value) || 1)} error={errori.corretteMinime} className="max-w-[320px]" />
          <Button variant="primary" icon={Save} onClick={onSalva} loading={salva.isPending} loadingText={admin.comuni.inCorso} className="self-start">
            {ta.salva}
          </Button>
        </>
      )}
    </section>
  );
}

function SezioneEsiti({ webinarId, dati }: { webinarId: string; dati: TestAdminDati }) {
  const ta = t.admin;
  const riepilogo = useRiepilogoTest(webinarId);
  const chiudi = useChiudiTest(webinarId);
  const toast = useToast();
  const [dialogo, setDialogo] = useState(false);
  const [q, setQ] = useState('');
  const [stato, setStato] = useState('');
  const [pagina, setPagina] = useState(1);
  const esiti = useEsitiTest(webinarId, q, stato, pagina);
  const r = riepilogo.data;
  const chiuso = r?.finestraChiusaA ? Date.parse(r.finestraChiusaA) <= Date.now() : false;

  const colonne = useMemo<ColumnDef<EsitoIscritto, unknown>[]>(
    () => [
      { header: ta.esiti.colonne.nome, cell: ({ row }) => `${row.original.cognome} ${row.original.nome}` },
      { header: ta.esiti.colonne.presenza, cell: ({ row }) => (row.original.percentualePresenza === null ? '—' : `${Math.floor(row.original.percentualePresenza)}%`) },
      { header: ta.esiti.colonne.ammesso, cell: ({ row }) => (row.original.ammesso ? ta.esiti.si : ta.esiti.no) },
      { header: ta.esiti.colonne.stato, cell: ({ row }) => <BadgeEsito e={row.original} /> },
      { header: ta.esiti.colonne.corrette, cell: ({ row }) => (row.original.tentativo?.corrette === null || row.original.tentativo?.corrette === undefined ? '—' : `${row.original.tentativo.corrette} / ${dati.domande.length}`) },
      { header: ta.esiti.colonne.quando, cell: ({ row }) => (row.original.tentativo?.consegnatoAt ? formattaDataOra(row.original.tentativo.consegnatoAt) : '—') },
    ],
    [ta, dati.domande.length],
  );

  async function chiudiOra() {
    try {
      await chiudi.mutateAsync();
      setDialogo(false);
      toast.mostra({ variant: 'success', title: ta.chiuso });
    } catch (e) {
      setDialogo(false);
      toast.mostra({ variant: 'error', title: e instanceof Error ? e.message : comuni.erroreGenerico });
    }
  }

  return (
    <section aria-labelledby="esiti-test" className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 id="esiti-test" className="t-title-2">
            {ta.riepilogo.titolo}
          </h2>
          <p className="t-body-small text-ink-muted">
            {r?.finestraChiusaA ? (chiuso ? ta.riepilogo.finestraChiusa(formattaOra(r.finestraChiusaA)) : ta.riepilogo.finestra(formattaOra(r.finestraChiusaA))) : ''} {ta.riepilogo.aggiornamento}
          </p>
        </div>
        {!chiuso && (
          <Button icon={Square} onClick={() => setDialogo(true)}>
            {ta.chiudi}
          </Button>
        )}
      </div>
      {riepilogo.isError && <Alert variant="error" title={riepilogo.error.message} />}
      {r && (
        <StatRow>
          <StatBlock label={ta.riepilogo.ammessi} value={r.ammessi} icon={Users} />
          <StatBlock label={ta.riepilogo.iniziati} value={r.iniziati} icon={Clock} />
          <StatBlock label={ta.riepilogo.superati} value={r.superati} icon={CircleCheck} tone="success" />
          <StatBlock label={ta.riepilogo.nonSuperati} value={r.nonSuperati} icon={CircleX} tone={r.nonSuperati > 0 ? 'error' : 'neutral'} />
          <StatBlock label={ta.riepilogo.scaduti} value={r.scaduti} icon={Clock} tone={r.scaduti > 0 ? 'warning' : 'neutral'} />
        </StatRow>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-4 items-end">
        <CampoRicerca valore={q} onCambia={(v) => { setQ(v); setPagina(1); }} label={ta.esiti.cerca} />
        <NativeSelect label={ta.esiti.filtro} placeholder={ta.esiti.tutti} value={stato} onChange={(e) => { setStato(e.target.value); setPagina(1); }} options={Object.entries(ta.esiti.stati).map(([value, label]) => ({ value, label }))} />
      </div>
      {esiti.isError && <Alert variant="error" title={esiti.error.message} />}
      <DataTable
        caption={ta.esiti.didascalia}
        columns={colonne}
        data={esiti.data?.elementi ?? []}
        getRowId={(e) => e.userId}
        loading={esiti.isPending}
        testi={admin.comuni}
        pagination={esiti.data ? { pagina: esiti.data.pagina, perPagina: esiti.data.perPagina, totale: esiti.data.totale, onPagina: setPagina } : undefined}
        empty={<EmptyState icon={Users} title={!q && !stato ? ta.esiti.nessuno : ta.esiti.nessunRisultato} />}
      />
      <Dialog
        open={dialogo}
        onOpenChange={(o) => !o && setDialogo(false)}
        title={ta.chiudiTitolo}
        description={ta.chiudiTesto}
        actions={
          <>
            <Button onClick={() => setDialogo(false)}>{admin.comuni.annulla}</Button>
            <Button variant="primary" onClick={chiudiOra} loading={chiudi.isPending} loadingText={admin.comuni.inCorso}>
              {ta.chiudiConferma}
            </Button>
          </>
        }
      />
    </section>
  );
}

function BadgeEsito({ e }: { e: EsitoIscritto }) {
  const b = t.admin.esiti.badge;
  if (!e.ammesso) return <Badge variant="neutral" icon={CircleX}>{b.non_ammesso}</Badge>;
  if (!e.tentativo) return <Badge variant="neutral" icon={Clock}>{b.non_iniziato}</Badge>;
  switch (e.tentativo.esito) {
    case 'superato':
      return <Badge variant="success" icon={CircleCheck}>{b.superato}</Badge>;
    case 'non_superato':
      return <Badge variant="error" icon={CircleX}>{b.non_superato}</Badge>;
    case 'scaduto':
      return <Badge variant="warning" icon={Clock}>{b.scaduto}</Badge>;
    default:
      return <Badge variant="accent" icon={Clock}>{b.in_corso}</Badge>;
  }
}
